import i18n from "../i18n";

export const ONE_MIN_MS = 60 * 1000;
export const ONE_HOUR_MS = 60 * ONE_MIN_MS;

function padTime(num: number) {
	return String(num).padStart(2, "0");
}

const DURATION_UNITS = ["second", "minute", "hour", "day"] as const;
type DurationUnit = (typeof DURATION_UNITS)[number];

function isDurationUnitLargerThan(a: DurationUnit, b: DurationUnit) {
	return DURATION_UNITS.indexOf(a) > DURATION_UNITS.indexOf(b);
}

function isDurationUnitLargerThanOrEqualTo(a: DurationUnit, b: DurationUnit) {
	return a === b || isDurationUnitLargerThan(a, b);
}

function getDurationUnitsWithinRange(min: DurationUnit, max: DurationUnit) {
	const minIndex = DURATION_UNITS.indexOf(min);
	const maxIndex = DURATION_UNITS.indexOf(max);
	if (minIndex < 0 || maxIndex < 0 || minIndex > maxIndex) {
		throw new Error(`Invalid duration unit range [${min}, ${max}]`);
	}
	return DURATION_UNITS.slice(minIndex, maxIndex + 1);
}

export function splitDurationByUnits(
	durationMs: number,
	unitRange: [DurationUnit, DurationUnit] = ["second", "hour"], // inclusive
): Record<DurationUnit, number> {
	const [minUnit] = unitRange;

	const totalSeconds = Math.round(durationMs / 1000);
	const totalMinutes = minUnit === "second" ? Math.floor(totalSeconds / 60) : Math.round(totalSeconds / 60);
	const totalHours = isDurationUnitLargerThanOrEqualTo("minute", minUnit)
		? Math.floor(totalMinutes / 60)
		: Math.round(totalMinutes / 60);
	const totalDays = isDurationUnitLargerThanOrEqualTo("hour", minUnit)
		? Math.floor(totalHours / 24)
		: Math.round(totalHours / 60);

	const allowedUnits = new Set(getDurationUnitsWithinRange(...unitRange));
	const days = allowedUnits.has("day") ? totalDays : 0;
	const hours = allowedUnits.has("hour") ? totalHours - days * 24 : 0;
	const minutes = allowedUnits.has("minute") ? totalMinutes - (days * 24 + hours) * 60 : 0;
	const seconds = allowedUnits.has("second") ? totalSeconds - ((days * 24 + hours) * 60 + minutes) * 60 : 0;

	return {
		second: seconds,
		minute: minutes,
		hour: hours,
		day: days,
	};
}

function getDurationUnitsNonZeroOrWithinRange(
	durationByUnits: Record<DurationUnit, number>,
	requiredUnitRange: [DurationUnit, DurationUnit] = ["minute", "minute"],
): [DurationUnit, DurationUnit] {
	const [minRequiredUnit, maxRequiredUnit] = requiredUnitRange;
	const minAvailableUnit = Object.entries(durationByUnits).find(([, value]) => value > 0)?.[0] as
		DurationUnit | undefined;
	const maxAvailableUnit = Object.entries(durationByUnits).findLast(([, value]) => value > 0)?.[0] as
		DurationUnit | undefined;
	return [
		!minAvailableUnit || isDurationUnitLargerThan(minAvailableUnit, minRequiredUnit)
			? minRequiredUnit
			: minAvailableUnit,
		!maxAvailableUnit || isDurationUnitLargerThan(maxRequiredUnit, maxAvailableUnit)
			? maxRequiredUnit
			: maxAvailableUnit,
	];
}

export function formatDuration(
	durationMs: number,
	{
		unitRange = ["second", "hour"],
		alwaysShownUnitRange = ["minute", "minute"],
	}: {
		unitRange?: [DurationUnit, DurationUnit]; // inclusive
		alwaysShownUnitRange?: [DurationUnit, DurationUnit]; // inclusive
	} = {},
) {
	const allowedUnits = new Set(getDurationUnitsWithinRange(...unitRange));
	const alwaysShownUnits = getDurationUnitsWithinRange(...alwaysShownUnitRange);
	if (alwaysShownUnits.some((unit) => !allowedUnits.has(unit))) {
		throw new Error(`alwaysShownUnitRange ${alwaysShownUnitRange} exceeds unitRange ${unitRange} bounds`);
	}

	const durationByUnits = splitDurationByUnits(durationMs, unitRange);

	return getDurationUnitsWithinRange(...getDurationUnitsNonZeroOrWithinRange(durationByUnits, alwaysShownUnitRange))
		.map((unit) => {
			const value = durationByUnits[unit];
			return unit === "day" ? value : padTime(value);
		})
		.reverse()
		.join(":");
}

/**
 * Given a time in ms, returns a screen reader friendly spoken string.
 * @param timeStr
 * @returns
 */
export function getDurationAriaLabelFromMs(ms: number): string {
	const totalSeconds = Math.floor(ms / 1000);

	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const segments = [];
	if (hours) {
		segments.push(i18n.t("result.components.studentSelection.videoPlayer.durationAriaLabel.hours", { count: hours }));
	}
	if (minutes || (!hours && !seconds)) {
		segments.push(
			i18n.t("result.components.studentSelection.videoPlayer.durationAriaLabel.minutes", { count: minutes }),
		);
	}
	if (seconds || (!hours && !minutes)) {
		segments.push(
			i18n.t("result.components.studentSelection.videoPlayer.durationAriaLabel.seconds", { count: seconds }),
		);
	}

	return segments.join(", ");
}

export function changeTimeOnly(oldDateTime: Date, hours: number, minutes: number) {
	const newDateTime = new Date(oldDateTime.getTime());
	newDateTime.setHours(hours);
	newDateTime.setMinutes(minutes);
	return newDateTime;
}

export function stripSeconds(date: Date) {
	const newDateTime = new Date(date.getTime());
	newDateTime.setSeconds(0);
	newDateTime.setMilliseconds(0);
	return newDateTime;
}

export function isBetweenTimes(target: Date, start: Date, end: Date) {
	return target.getTime() >= start.getTime() && target.getTime() <= end.getTime();
}

type TimezoneFormat = "long" | "short" | "shortOffset" | "longOffset" | "shortGeneric" | "longGeneric";

export const DEFAULT_TIMEZONE = "America/Toronto";

export function formatTimezoneName(timezone: string, format: TimezoneFormat = "short"): string {
	return (
		Intl.DateTimeFormat(undefined, { timeZone: timezone, timeZoneName: format })
			.formatToParts()
			.find(({ type }) => type === "timeZoneName")?.value ?? ""
	);
}

export function getTimezoneOffset(timezone: string) {
	const offsetName = formatTimezoneName(timezone, "longOffset");
	const withoutGmt = offsetName.substring(3);
	if (!withoutGmt) {
		return 0;
	}
	const [hours, minutes] = withoutGmt.split(":");
	let fractionalHours = parseInt(hours, 10);
	if (minutes) {
		fractionalHours += parseInt(minutes, 10) / 60;
	}
	return fractionalHours;
}
